import { Identifiable, Reference } from '@approvalmax/types';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { ItemForEdit } from 'pages/requestForm/components';
import {
    NetSuiteExpenseCategory,
    NetSuiteItem,
    NetSuiteSalesTaxItem,
} from 'pages/requestForm/data/netsuite/NetSuiteContext';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode, RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { useCallback, useMemo, useState } from 'react';

import {
    NetSuiteLineItemAmortizationScheduleCell,
    NetSuiteLineItemCategoryCell,
    NetSuiteLineItemCustomerCell,
    NetSuiteLineItemCustomReferenceCell,
    NetSuiteLineItemDropdownCell,
    NetSuiteLineItemTaxCodeCell,
} from '../../components';
import { NetSuiteLineItemItemCell } from '../../components/NetSuiteLineItemItemCell/NetSuiteLineItemItemCell';
import { NetSuiteLineItemTaxAccountCell } from '../../components/NetSuiteLineItemTaxAccountCell/NetSuiteLineItemTaxAccountCell';
import { messages } from './useGroupEditItems.messages';
import { GroupEditItemsField, UseGroupEditItemsParams } from './useGroupEditItems.types';

export const useGroupEditItems = (params: UseGroupEditItemsParams) => {
    const {
        items,
        accounts,
        currencies,
        departments,
        classes,
        locations,
        workflowId,
        availableFields,
        availableCustomFields,
        onRemoveLine,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateCustomField,
        onItemChange,
        onChangeExpenseCategory,
        onChangeCurrency,
        onChangeTaxCode,
    } = params;

    const [isGroupEditPopupVisible, setIsGroupEditPopupVisible] = useState(false);
    const selectedItems = useMemo(() => items.filter((item) => item.checked), [items]);

    const request = useSelector(requestSelector);
    const integrationCode = request.integrationCode as domain.NetSuiteIntegrationCodeType;
    const editMode = useSelector((state) => getRequestEditMode(state, request));

    const handleGroupRemove = useCallback(() => {
        selectedItems.forEach((lineItem) => onRemoveLine(lineItem.id));
    }, [onRemoveLine, selectedItems]);

    const handleGroupClone = useCallback(() => {
        selectedItems.forEach((lineItem) => {
            onCloneLine(lineItem.id);
        });
        onMultiLineFieldUpdate('checked', false);
    }, [onMultiLineFieldUpdate, onCloneLine, selectedItems]);

    const openGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(true);
    }, []);

    const closeGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(false);
        onMultiLineFieldUpdate('checked', false);
    }, [onMultiLineFieldUpdate]);

    const groupItems = useMemo(() => {
        const itemsSingleValue: ItemForEdit<Identifiable>[] = [];
        const itemsMultipleValue: ItemForEdit<Identifiable[]>[] = [];

        availableFields.forEach((field) => {
            switch (field) {
                case GroupEditItemsField.Item: {
                    itemsSingleValue.push({
                        id: 'item',
                        name: messages.itemColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemItemCell
                                value={value}
                                onChange={onChange}
                                placeholder={messages.itemFieldPlaceholder}
                            />
                        ),
                        onSave: (value: NetSuiteItem) => {
                            selectedItems.forEach((lineItem) => onItemChange?.(lineItem.id, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.Category: {
                    itemsSingleValue.push({
                        id: 'category',
                        name: messages.categoryColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemCategoryCell
                                value={value}
                                onChange={onChange}
                                placeholder={messages.categoryFieldPlaceholder}
                            />
                        ),
                        onSave: (value: NetSuiteExpenseCategory) => {
                            selectedItems.forEach((lineItem) => onChangeExpenseCategory?.(lineItem, value));
                        },
                        prepareChange: (value: NetSuiteExpenseCategory, values) => {
                            const res: Record<string, Identifiable | null> = {
                                ...values,
                                category: value,
                            };

                            const relatedAccount = accounts?.find((account) => account.id === value.accountId);

                            if (!relatedAccount) {
                                return res;
                            }

                            if (availableFields.includes(GroupEditItemsField.ExpenseAccount)) {
                                res.expenseAccount = relatedAccount;
                            } else {
                                res.account = relatedAccount;
                            }

                            return res;
                        },
                    });

                    return;
                }

                case GroupEditItemsField.Account:
                case GroupEditItemsField.ExpenseAccount: {
                    if (selectedItems.every((lineItem) => !lineItem.account)) {
                        itemsSingleValue.push({
                            id: field,
                            name: messages.accountColumnName,
                            render: ({ value, onChange, values }) => (
                                <NetSuiteLineItemDropdownCell
                                    items={accounts || []}
                                    value={value}
                                    onChange={onChange}
                                    disabled={Boolean(values.category)}
                                    placeholder={messages.accountFieldPlaceholder}
                                />
                            ),
                            onSave: (value: Reference) => {
                                selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                            },
                        });
                    }

                    return;
                }

                case GroupEditItemsField.Currency: {
                    itemsSingleValue.push({
                        id: 'currency',
                        name: messages.columnCurrency,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemDropdownCell
                                items={currencies || []}
                                value={value}
                                onChange={onChange}
                                placeholder={messages.columnCurrencyPlaceholder}
                            />
                        ),
                        onSave: (value: domain.NetSuiteCurrency) => {
                            selectedItems.forEach((lineItem) => onChangeCurrency?.(lineItem, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.TaxCode: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.taxCodeColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemTaxCodeCell
                                value={value}
                                onChange={onChange}
                                placeholder={messages.taxCodeFieldPlaceholder}
                            />
                        ),
                        onSave: (value: NetSuiteSalesTaxItem) => {
                            selectedItems.forEach(
                                (lineItem) =>
                                    onChangeTaxCode?.(lineItem, value) ?? onFieldUpdate(lineItem.id, field, value)
                            );
                        },
                    });

                    return;
                }

                case GroupEditItemsField.Class: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.classColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemDropdownCell
                                items={classes}
                                value={value}
                                onChange={onChange}
                                placeholder={messages.classFieldPlaceholder}
                            />
                        ),
                        onSave: (value: Reference) => {
                            selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.Department: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.departmentColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemDropdownCell
                                items={departments}
                                value={value}
                                onChange={onChange}
                                placeholder={messages.departmentFieldPlaceholder}
                            />
                        ),
                        onSave: (value: Reference) => {
                            selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.Location: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.locationColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemDropdownCell
                                items={locations}
                                value={value}
                                onChange={onChange}
                                placeholder={messages.locationFieldPlaceholder}
                            />
                        ),
                        onSave: (value: Reference) => {
                            selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.Customer: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.customerColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemCustomerCell
                                value={value}
                                onChange={onChange}
                                placeholder={messages.selectDefaultPlaceholder}
                                sublistType={
                                    integrationCode === domain.IntegrationCode.NetSuiteJournalEntry
                                        ? domain.DocumentSublistType.LineList
                                        : domain.DocumentSublistType.ExpenseList
                                }
                            />
                        ),
                        onSave: (value: Reference) => {
                            selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.TaxAccount: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.taxAccountColumnName,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemTaxAccountCell
                                value={value}
                                onChange={onChange}
                                placeholder={messages.taxAccountFieldPlaceholder}
                                integrationCode={integrationCode}
                            />
                        ),
                        onSave: (value: Reference) => {
                            selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                        },
                    });

                    return;
                }

                case GroupEditItemsField.AmortizationSchedule: {
                    itemsSingleValue.push({
                        id: field,
                        name: messages.amortizationSchedule,
                        render: ({ value, onChange }) => (
                            <NetSuiteLineItemAmortizationScheduleCell
                                value={value}
                                onChange={onChange}
                                placeholder={messages.selectDefaultPlaceholder}
                                apiVersion={
                                    integrationCode === domain.IntegrationCode.NetSuiteJournalEntry ? 'v2' : 'v1'
                                }
                                integrationCode={integrationCode}
                            />
                        ),
                        onSave: (value: Reference) => {
                            selectedItems.forEach((lineItem) => onFieldUpdate(lineItem.id, field, value));
                        },
                    });

                    return;
                }

                default: {
                    return;
                }
            }
        });

        availableCustomFields.forEach((field) => {
            const isEditable = field.workflows?.some(
                (workflow) => workflow.workflowId === workflowId && workflow.isEditable
            );

            if (!isEditable) {
                return;
            }

            if (field.netSuiteField?.type === 'ListRecord') {
                itemsSingleValue.push({
                    id: field.id,
                    name: field.name,
                    render: ({ value, onChange }) => (
                        <NetSuiteLineItemCustomReferenceCell
                            value={value}
                            placeholder={messages.selectDefaultPlaceholder}
                            onChange={onChange}
                            fieldId={field.id}
                            sublistType={domain.DocumentSublistType.ExpenseList}
                        />
                    ),
                    onSave: (value: Reference) => {
                        selectedItems.forEach((lineItem) => updateCustomField(lineItem.id, field, 'valueRef', value));
                    },
                });
            }

            if (field.netSuiteField?.type === 'MultipleSelect') {
                itemsMultipleValue.push({
                    id: field.id,
                    name: field.name,
                    render: ({ value, onChange }) => (
                        <NetSuiteLineItemCustomReferenceCell
                            value={value}
                            placeholder={messages.selectDefaultPlaceholder}
                            onChange={onChange}
                            multiple
                            fieldId={field.id}
                            sublistType={domain.DocumentSublistType.ExpenseList}
                        />
                    ),
                    onSave: (value: Reference[]) => {
                        selectedItems.forEach((lineItem) => updateCustomField(lineItem.id, field, 'valueRefs', value));
                    },
                });
            }
        });

        return [...itemsSingleValue, ...itemsMultipleValue];
    }, [
        availableFields,
        availableCustomFields,
        selectedItems,
        onItemChange,
        onChangeExpenseCategory,
        accounts,
        onFieldUpdate,
        currencies,
        onChangeCurrency,
        onChangeTaxCode,
        classes,
        departments,
        locations,
        integrationCode,
        workflowId,
        updateCustomField,
    ]);

    const isEditorMode = editMode === RequestEditMode.Editor;
    const isApproverMode = editMode === RequestEditMode.Approver;

    const isGroupRemoveVisible = !isApproverMode && !isEditorMode;
    const isGroupCloneVisible = !isApproverMode;
    const isGroupEditVisible = groupItems.length > 0;

    const isShowGroupButtons =
        selectedItems.length > 0 && (isGroupRemoveVisible || isGroupCloneVisible || isGroupEditVisible);

    return {
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        groupItems,
    };
};
